import React, { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { CollapseBlock } from '../../../../common/index.jsx';
import { Spinner } from '../../../../common/index.jsx';
import { routes } from '../../../../../utils/helpers/ApiClient.jsx';
import { AppBarCMS } from '../../../../basic/index.jsx';
import {
  useDeleteErasmusApplicationMutation,
  useGetErasmusApplicationsQuery,
  useGetErasmusApplicationDetailQuery,
  useSaveErasmusApplicationMutation,
  useSaveErasmusApplicationStatusMutation,
  useGetApplicationStatusQuery,
} from '../../../../../redux/services/erasmusApplications';
import ErasmusApplicationsTable from './ErasmusApplicationsTable';
import { isNull } from 'lodash';
import { Tooltip, Select,  Typography, Space, Button, Flex, Row, Col } from 'antd';
import { SortAscendingOutlined } from '@ant-design/icons';

import MobilityModal from './MobilityModal/MobilityModal';
import StatusTimelineModal from './StatusTimelineModal/StatusTimelineModal';
import { useGenerateErasmusPDFMutation } from '../../../../../redux/services/erasmusApplications';

import useIsUserAdmin from '../../../../../hooks/useIsUserAdmin.jsx';
import useIsUserErasmusKoordinator from '../../../../../hooks/useIsUserErasmusKoordinator';
import moment from 'moment';
import { useGetErasmusUniversitiesQuery } from '../../../../../redux/services/erasmusUniversities';
import { useGetErasmusChallengesQuery } from '../../../../../redux/services/erasmusChallenges';
import { useToastMessages } from '../../../../../hooks/useToastMessages';

// utils: jednoduchý filter bez diakritiky
const removeDiacritics = (s = '') =>
  s
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase();

const getChallengeLabel = (challenge) =>
  [challenge?.name, challenge?.academic_year].filter(Boolean).join(' - ');

const ErasmusApplicationsPage = ({ uniqueTableId }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const isAdmin = useIsUserAdmin();
  const isErasmusKoordinator = useIsUserErasmusKoordinator();
  //const isArchive = mode === 'archive';
  const [selectedOption, setSelectedOption] = useState('active');
  const [selectedChallenge, setSelectedChallenge] = useState('');
  const isArchive = selectedOption === 'archived';

  const {
    deleteSuccess,
    apiErrorMessage,
    successMessage,
  } = useToastMessages();

  const [applicationInPreviewId, setApplicationInPreviewId] = useState(null);
  const [applicationToStatusChangeId] = useState(null);
  const [applicationTimelineId, setApplicationTimelineId] = useState(null);
  const [pdfGeneratingId, setPdfGeneratingId] = useState(null);
  const [expandedRowKeys, setExpandedRowKeys] = useState([]);
  const [draftChoosedUniversityById, setDraftChoosedUniversityById] = useState(
    {},
  );

  const {
    data: erasmusApplications,
    isLoading,
    refetch,
  } = useGetErasmusApplicationsQuery();

  //const [ deleteErasmusApplication, { isSuccess }] = useDeleteErasmusApplicationMutation();
  const [applications, setApplications] = useState(erasmusApplications);
  const { data: erasmusUniversities } = useGetErasmusUniversitiesQuery(true);
  const { data: erasmusChallenges } = useGetErasmusChallengesQuery();
  const universityOptions = erasmusUniversities
    ? erasmusUniversities.map((uni) => ({
        label: `${uni.name} (${uni.city}, ${uni.country})`, // Čo sa zobrazí
        value: uni.id, // Hodnota,ktorá sa uloží
      }))
    : [];

  const challengeOptions = useMemo(() => {
    if (!erasmusChallenges) return [];

    const allowedChallengeIds =
      isAdmin || isErasmusKoordinator
        ? null
        : new Set(
            (applications || [])
              .map((application) => application.challenge_id)
              .filter(Boolean),
          );

    const availableChallenges = allowedChallengeIds
      ? erasmusChallenges.filter((challenge) =>
          allowedChallengeIds.has(challenge.id),
        )
      : erasmusChallenges;

    return [
      ...availableChallenges.map((challenge) => ({
        label: getChallengeLabel(challenge),
        value: challenge.id,
      })),
      { label: 'Všetky', value: '' },
    ];
  }, [applications, erasmusChallenges, isAdmin, isErasmusKoordinator]);

  useEffect(() => {
    if (
      selectedChallenge &&
      !challengeOptions.some((option) => option.value === selectedChallenge)
    ) {
      setSelectedChallenge('');
    }
  }, [challengeOptions, selectedChallenge]);

  const { isFetching: isLoadingApplicationPreview } =
    useGetErasmusApplicationDetailQuery(applicationInPreviewId || applicationToStatusChangeId, {skip: isNull(applicationInPreviewId) && isNull(applicationToStatusChangeId),});

  const {
    data: applicationStatusHisoty,
    isFetching: isLoadingApplicationStatusHistory,
  } = useGetApplicationStatusQuery(applicationTimelineId, {
    skip: isNull(applicationTimelineId),
  });

  const [generateApplicationPDF] =
    useGenerateErasmusPDFMutation();

  const [
    deleteErasmusApplication,
    { isSuccess: isDeleteSuccess, error: deleteError },
  ] = useDeleteErasmusApplicationMutation();

  const [
    saveErasmusApplication,
    { isSuccess: isSaveSuccess, error: saveError },
  ] = useSaveErasmusApplicationMutation();

  const [saveErasmusApplicationStatus, { error: saveStatusError }] =
    useSaveErasmusApplicationStatusMutation();

  const onStatusChangeFun = (record, StatusValue, note) => {
    const values = {
      ...record,
      applicationStatus: StatusValue,
      study_program: record.study_program['id'],
    };

    const erasmusStatus = {
      application_id: record.id,
      applicationStatus: StatusValue,
      note: note?.trim() || null,
    };

    saveErasmusApplication({ body: values, id: record.id });
    saveErasmusApplicationStatus({ body: erasmusStatus });
  };

  const onArchiveFun = (id) => {
    const application = applications.find(
      (application) => application.id === id,
    );
    const values = {
      ...application,
      archived_at: moment().format('YYYY-MM-DD HH:mm:ss'),
      study_program: application.study_program['id'],
    };

    saveErasmusApplication({ body: values, id: id });
  };

  const universityActionSuccessMessage = useRef(null);

  const updateApplicationLocally = (applicationId, updater) => {
    setApplications((currentApplications) =>
      (currentApplications || []).map((application) =>
        application.id === applicationId ? updater(application) : application,
      ),
    );
  };

  const setDraftChoosedUniversity = (applicationId, universityId) => {
    setDraftChoosedUniversityById((current) => ({
      ...current,
      [applicationId]: universityId,
    }));
  };

  const clearDraftChoosedUniversity = (applicationId) => {
    setDraftChoosedUniversityById((current) => {
      const next = { ...current };
      delete next[applicationId];
      return next;
    });
  };

  const collapseExpandedRow = (applicationId) => {
    setExpandedRowKeys((current) =>
      current.filter((key) => String(key) !== String(applicationId)),
    );
  };

  const onChoosedUniversityChange = (record, choosedUniversityId) => {
    universityActionSuccessMessage.current = t(
      'Úspešne zmenená výberová univerzita prihlášky',
    );

    const nextChoosedUniversity =
      erasmusUniversities?.find((uni) => uni.id === choosedUniversityId) ?? null;

    updateApplicationLocally(record.id, (application) => ({
      ...application,
      choosed_university_id: choosedUniversityId,
      choosed_university: nextChoosedUniversity,
      requested_choosed_university_id: null,
      requested_choosed_university: null,
    }));
    clearDraftChoosedUniversity(record.id);

    const body = {
      ...record,
      choosed_university_id: choosedUniversityId,
      requested_choosed_university_id: null,
      study_program: record.study_program['id'],
    };

    saveErasmusApplication({ body: body, id: record.id });
  };

  const onChallengeChange = (record, challengeId) => {
    universityActionSuccessMessage.current = t(
      'Úspešne zmenená výzva prihlášky',
    );

    const body = {
      ...record,
      challenge_id: challengeId,
      study_program: record.study_program?.id ?? record.study_program,
    };

    saveErasmusApplication({ body, id: record.id });
  };

  const onApproveRequestedUniversityChange = (record) => {
    if (!record?.requested_choosed_university_id) {
      return;
    }

    universityActionSuccessMessage.current = t(
      'Úspešne potvrdená požiadavka na zmenu univerzity',
    );

    const nextChoosedUniversity =
      erasmusUniversities?.find(
        (uni) => uni.id === record.requested_choosed_university_id,
      ) ?? record.requested_choosed_university ?? null;

    updateApplicationLocally(record.id, (application) => ({
      ...application,
      choosed_university_id: record.requested_choosed_university_id,
      choosed_university: nextChoosedUniversity,
      requested_choosed_university_id: null,
      requested_choosed_university: null,
    }));
    clearDraftChoosedUniversity(record.id);

    const body = {
      ...record,
      choosed_university_id: record.requested_choosed_university_id,
      requested_choosed_university_id: null,
      study_program: record.study_program['id'],
    };

    saveErasmusApplication({ body, id: record.id });
  };

  const onRejectRequestedUniversityChange = (record) => {
    if (!record?.requested_choosed_university_id) {
      return;
    }

    universityActionSuccessMessage.current = t(
      'Požiadavka na zmenu univerzity bola zamietnutá',
    );

    updateApplicationLocally(record.id, (application) => ({
      ...application,
      university1_id: application.choosed_university_id,
      university1: application.choosed_university,
      requested_choosed_university_id: null,
      requested_choosed_university: null,
    }));
    clearDraftChoosedUniversity(record.id);

    const body = {
      ...record,
      university1_id: record.choosed_university_id,
      requested_choosed_university_id: null,
      study_program: record.study_program['id'],
    };

    saveErasmusApplication({ body, id: record.id });
  };

  useEffect(() => {
    if (isDeleteSuccess) {
      deleteSuccess();
      refetch();
    } else if (isSaveSuccess) {
      if (universityActionSuccessMessage.current) {
        successMessage({
          message: universityActionSuccessMessage.current,
        });
        universityActionSuccessMessage.current = null;
      } else {
        successMessage({
          message: t('Úspešne zmenený status prihlášky'),
        });
      }
      refetch();
    }

    if (deleteError) {
      apiErrorMessage(deleteError);
    }

    if (saveError) {
      apiErrorMessage(saveError);
    }

    if (saveStatusError) {
      apiErrorMessage(saveStatusError);
    }
  }, [
    isDeleteSuccess,
    isSaveSuccess,
    deleteError,
    saveError,
    saveStatusError,
  ]);

  useEffect(() => {
    if (erasmusApplications) {
      setApplications(erasmusApplications);
    }
  }, [erasmusApplications]);

  useEffect(() => {
    if (applications) {
      setDraftChoosedUniversityById((current) => {
        const next = { ...current };
        applications.forEach((application) => {
          if (next[application.id] === undefined) {
            next[application.id] = application.choosed_university?.id;
          }
        });
        return next;
      });
    }
  }, [applications]);

  const secondaryCondition = useCallback(
    (record) => record.applicationStatus === 'Rozpracovaná',
    [],
  );

  const disableExpandRow = useCallback(
    (record) => record.applicationStatus === 'Akceptovaná',
    [],
  );

  // 3 stĺpcový layout pre položky v dropdown-e
  const rowStyles = {
    row: {
      display: 'grid',
      gridTemplateColumns: '2fr 1fr 1fr',
      gap: 8,
      alignItems: 'center',
    },
    colName: {
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
      fontWeight: 500,
    },
    col: {
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
    },
    headerWrap: {
      position: 'sticky',
      top: 0,
      zIndex: 1,
      fontWeight: 600,
      padding: '6px 12px',
      borderBottom: '1px solid #f0f0f0',
      marginBottom: 4,
      background: '#fff',
      color: 'inherit',
    },
  };

  const getUniOptionsForRow = (record, universities) => {
    const p1 = record?.university1_id;
    const p2 = record?.university2_id;
    const p3 = record?.university3_id;

    const priorityOf = (id) => {
      if (id === p1) return 1;
      if (id === p2) return 2;
      if (id === p3) return 3;
      return 99; ///ostatne budu za vybranimi
    };

    const badge = (rank) =>
      rank === 1 ? '① ' : rank === 2 ? '② ' : rank === 3 ? '③ ' : '';

    const list = Array.isArray(universities) ? universities : [];

    const opts = list.map((u) => {
      const name = u?.name ?? '';
      const city = u?.city ?? '';
      const country = u?.country ?? '';
      const plainLabel = `${name} (${city}, ${country})`;
      const rank = priorityOf(u.id);
      const isPinned = rank < 99;

      return {
        value: u.id,

        // ReactNode v dropdown-e (3 stĺpce + zvýraznenie + odznak ①/②/③)
        label: (
          <div
            style={{
              ...rowStyles.row,
              ...(isPinned ? rowStyles.pinnedRow : {}),
            }}
          >
            <span
              style={{
                ...rowStyles.colName,
                ...(isPinned ? { fontWeight: 600 } : {}),
              }}
              title={name}
            >
              {badge(rank)}
              {name}
            </span>
            <span style={rowStyles.col} title={city}>
              {city}
            </span>
            <span style={rowStyles.col} title={country}>
              {country}
            </span>
          </div>
        ),

        // čo sa ukáže po výbere v inpute
        plainLabel,


        // na sort
        priority: rank,
        sortKey: removeDiacritics(plainLabel),
      };
    });

    //  poradie: 1 → 2 → 3 → ostatné(tie potom abecedne)
    opts.sort(
      (a, b) => a.priority - b.priority || a.sortKey.localeCompare(b.sortKey),
    );

    return opts;
  };

  return isLoading || !universityOptions ? (
    <Spinner />
  ) : (
    <>
      <AppBarCMS
        title={!isArchive ? t('Správa prihlášok') : t('Archív')}
        backUrl={routes.cmsDashboard}
        backUrlTooltip={t('Nástenka')}
        createText={t('Nová prihláška')}
        createUrl={!isArchive ? routes.cmsErasmusApplicationCreate : undefined}
        permissionsBasic={'erasmus-applications'} // TODO: Make separate permission for each action
        mainActions={
          !isArchive
            ? [
                {
                  onClick: () => navigate(routes.cmsErasmusApplicationOrder),
                  icon: (
                    <Tooltip
                      title={t('Zoradiť prihlášky')}
                      placement="bottomLeft"
                    >
                      <SortAscendingOutlined />
                    </Tooltip>
                  ),
                  permission: 'erasmus-applications.destroy',
                },
              ]
            : undefined
        }
      />
      <div style={{ margin: '10px' }}>
        <CollapseBlock>
          <ErasmusApplicationsTable
            key={isArchive ? 'archive-view' : 'active-view'}
            uniqueTableId={uniqueTableId}
            erasmusApplications={applications}
            selectedOption={selectedOption}
            setSelectedOption={setSelectedOption}
            selectedChallenge={selectedChallenge}
            setSelectedChallenge={setSelectedChallenge}
            challengeOptions={challengeOptions}
            pdfGeneratingId={pdfGeneratingId}
            expandedRowKeys={expandedRowKeys}
            onExpandedRowsChange={(keys) => {
              setExpandedRowKeys(keys);
            }}
            onDelete={
              !isArchive && (isErasmusKoordinator || isAdmin)
                ? (record) => deleteErasmusApplication(record.id)
                : undefined
            }
            onDetailClick={
              !isArchive
                ? (record) => {
                    if (record.applicationStatus === 'Rozpracovaná') {
                      return navigate(
                        `${routes.cmsErasmusApplicationEdit}/${record.id}`,
                      );
                    }
                    return undefined;
                  }
                : undefined
            }
            secondaryCondition={secondaryCondition}
            disableExpandRow={disableExpandRow}
            onPreviewClick={(record) => {
              setApplicationInPreviewId(record.id);
            }}
            onCancelClick={
              !isAdmin && !isArchive && !isErasmusKoordinator
                ? (record) => {
                    if (record.applicationStatus !== 'Zrušená') {
                      return onStatusChangeFun(record, 'Zrušená');
                    }
                    return undefined;
                  }
                : undefined
            }
            onTimelineClick={(record) => {
              setApplicationTimelineId(record.id);
            }}
            onArchiveClick={
              !isArchive
                ? (record) => {
                    onArchiveFun(record.id);
                  }
                : undefined
            }
            onSubmitClick={
              !isAdmin && !isArchive && !isErasmusKoordinator
                ? (record) =>
                    record.applicationStatus !== 'Odoslaná'
                      ? onStatusChangeFun(record, 'Odoslaná')
                      : undefined
                : undefined
            }
            onChangeStateClick={
              !isArchive && (isErasmusKoordinator || isAdmin)
                ? (record, key, note) => {
                    onStatusChangeFun(record, key, note);
                  }
                : undefined
            }
            onChangeChallengeClick={
              !isArchive && (isErasmusKoordinator || isAdmin)
                ? (record, challengeId) => {
                    onChallengeChange(record, challengeId);
                  }
                : undefined
            }
            onPdfClick={async (record) => {
              setPdfGeneratingId(record.id);
              try {
                const response = await generateApplicationPDF(record).unwrap();

                // Dekódovanie Base64 reťazca
                const byteCharacters = atob(response.pdf);
                const byteNumbers = new Array(byteCharacters.length);
                for (let i = 0; i < byteCharacters.length; i++) {
                  byteNumbers[i] = byteCharacters.charCodeAt(i);
                }
                const byteArray = new Uint8Array(byteNumbers);
                const blob = new Blob([byteArray], { type: 'application/pdf' });

                const url = window.URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                // a.download = 'application.pdf';
                a.download = `${record.person?.last_name || ''} ${record.person?.first_name || ''}[${record.mobility_year}]_${record.choosed_university.name}_[${record.mobility_semester}].pdf`;
                document.body.appendChild(a);
                a.click();

                document.body.removeChild(a);
                window.URL.revokeObjectURL(url);
              } catch (error) {
                apiErrorMessage(error);
              } finally {
                setPdfGeneratingId(null);
              }
            }}
            customExpandedRowRender={
              (isErasmusKoordinator || isAdmin) && !isArchive
                ? (record) => {
                    const optionsForRow = getUniOptionsForRow(
                      record,
                      erasmusUniversities,
                    );
                    const draftChoosedUniversityId =
                      draftChoosedUniversityById[record.id] ??
                      record?.choosed_university?.id;
                    const hasUniversityDraftChange =
                      draftChoosedUniversityId !== record?.choosed_university?.id;
                    return (
                      <Row gutter={[16, 12]} align="stretch">
                        {record?.requested_choosed_university ? (
                          <Col xs={24} lg={12}>
                            <div
                              style={{
                                border: '1px solid #ffd591',
                                background: '#fff7e6',
                                borderRadius: 8,
                                padding: '12px 14px',
                                height: '100%',
                              }}
                            >
                              <Flex vertical gap={10}>
                                <Typography.Text strong style={{ fontSize: 13 }}>
                                  {t('Požiadavka študenta')}
                                </Typography.Text>
                                <div>
                                  <Typography.Text style={{ fontSize: 13 }}>
                                    <Typography.Text strong>
                                      {t('Aktuálne')}:
                                    </Typography.Text>{' '}
                                    {record?.choosed_university?.name || '-'}
                                  </Typography.Text>
                                  <br />
                                  <Typography.Text style={{ fontSize: 13 }}>
                                    <Typography.Text strong>
                                      {t('Požadovaná')}:
                                    </Typography.Text>{' '}
                                    {record?.requested_choosed_university?.name || '-'}
                                  </Typography.Text>
                                </div>
                                <Typography.Text
                                  type="secondary"
                                  style={{ fontSize: 12 }}
                                >
                                  {t('Po akcii sa požiadavka vyčistí a upozornenie zmizne.')}
                                </Typography.Text>
                                <Space wrap size={8}>
                                  <Button
                                    type="primary"
                                    size="small"
                                    onClick={() =>
                                      onApproveRequestedUniversityChange(record)
                                    }
                                  >
                                    {t('Potvrdiť')}
                                  </Button>
                                  <Button
                                    size="small"
                                    onClick={() =>
                                      onRejectRequestedUniversityChange(record)
                                    }
                                  >
                                    {t('Zamietnuť')}
                                  </Button>
                                </Space>
                              </Flex>
                            </div>
                          </Col>
                        ) : null}

                        <Col xs={24} lg={record?.requested_choosed_university ? 12 : 24}>
                          <div
                            style={{
                              border: '1px solid #f0f0f0',
                              borderRadius: 8,
                              padding: '12px 14px',
                              background: '#fff',
                              height: '100%',
                            }}
                          >
                            <Flex vertical gap={10}>
                              <Typography.Text type="secondary" strong>
                                {t('Nominovaný na')}
                              </Typography.Text>
                              <Select
                                size="small"
                                value={draftChoosedUniversityId}
                                onChange={(choosedUniId) =>
                                  setDraftChoosedUniversity(record.id, choosedUniId)
                                }
                                style={{ width: '100%' }}
                                showSearch={{
                                  filterOption: (input, option) => {
                                    const q = removeDiacritics(input || '');
                                    const hay = removeDiacritics(
                                      (option?.plainLabel || '').toLowerCase(),
                                    );
                                    return !q || hay.includes(q);
                                  },
                                }}
                                popupRender={(menu) => (
                                  <>
                                    <div style={rowStyles.headerWrap}>
                                      <div style={rowStyles.row}>
                                        <span>{t('Názov')}</span>
                                        <span>{t('Mesto')}</span>
                                        <span>{t('Krajina')}</span>
                                      </div>
                                    </div>
                                    {menu}
                                  </>
                                )}
                                options={optionsForRow}
                              />
                              <Space wrap size={8}>
                                <Button
                                  type="primary"
                                  size="small"
                                  disabled={!hasUniversityDraftChange}
                                  onClick={() =>
                                    onChoosedUniversityChange(
                                      record,
                                      draftChoosedUniversityId,
                                    )
                                  }
                                >
                                  {t('Zmeniť')}
                                </Button>
                                <Button
                                  size="small"
                                  onClick={() => {
                                    setDraftChoosedUniversity(
                                      record.id,
                                      record?.choosed_university?.id,
                                    );
                                    collapseExpandedRow(record.id);
                                  }}
                                >
                                  {t('Zrušiť')}
                                </Button>
                              </Space>
                            </Flex>
                          </div>
                        </Col>
                      </Row>
                    );
                  }
                : undefined
            }
          />
        </CollapseBlock>
      </div>

      {!isNull(applicationInPreviewId) && (
        <MobilityModal
          visible={!isNull(applicationInPreviewId)}
          toggleModal={() => setApplicationInPreviewId(null)}
          title={t('Detail prihlášky')}
          id={applicationInPreviewId}
          isLoading={isLoadingApplicationPreview}
        />
      )}

      {!isNull(applicationTimelineId) && (
        <StatusTimelineModal
          visible={!isNull(applicationTimelineId)}
          toggleModal={() => setApplicationTimelineId(null)}
          title={t('História stavov prihlášky')}
          data={applicationStatusHisoty}
          isLoading={isLoadingApplicationStatusHistory}
        />
      )}
    </>
  );
};

export default ErasmusApplicationsPage;
